import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, btnP, inpS, sp, rad, type, icBox, listRow } from "../../constants/styles";
import Ic from "../common/Ic";
import Fld from "../common/Fld";

export default function TeamPage() {
  const { t, h, go, flash, showConfirm, currUserId } = useContext(Ctx);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("manager");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editRole, setEditRole] = useState("");

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    const data = await api.getTeam();
    if (data.success) setMembers(data.members);
    setLoading(false);
  };

  const handleAdd = async () => {
    if (!name || !phone || !password) { flash(t.fillRequired || "Fill all fields", "error"); return; }
    setSaving(true);
    const res = await api.addTeamMember({ name, phone, password, role });
    if (res.success) {
      setMembers([...members, res.member]);
      setShowAdd(false); setName(""); setPhone(""); setPassword(""); setRole("manager");
      flash(t.teamMemberAdded || "Team member added!");
    } else {
      flash(res.error || "Failed to add", "error");
    }
    setSaving(false);
  };

  const handleUpdateRole = async (id) => {
    if (!editRole) return;
    const res = await api.updateTeamMember(id, { role: editRole });
    if (res.success) {
      setMembers(members.map(m => m.id === id ? { ...m, role: editRole } : m));
      setEditId(null);
      flash(t.updated || "Updated!");
    }
  };

  const handleToggleActive = async (member) => {
    const newActive = member.active ? 0 : 1;
    const res = await api.updateTeamMember(member.id, { active: newActive });
    if (res.success) {
      setMembers(members.map(m => m.id === member.id ? { ...m, active: newActive } : m));
      flash(newActive ? "Activated" : "Deactivated");
    }
  };

  const handleRemove = (member) => {
    showConfirm(`Remove ${member.name} from your team?`, async () => {
      const res = await api.removeTeamMember(member.id);
      if (res.success) {
        setMembers(members.filter(m => m.id !== member.id));
        flash(t.deleted || "Removed!");
      } else {
        flash(res.error || "Failed", "error");
      }
    });
  };

  const roleBadge = (r) => {
    const colors = { owner: h.ac, manager: h.gn, viewer: h.wn };
    const c = colors[r] || h.ts;
    return bdg(`${c}15`, c);
  };

  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: "center" }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div>;
  }

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    <button onClick={() => go("settings")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> {t.back || "Back"}
    </button>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.lg }}>
      <h2 style={{ ...type.h1, color: h.tx, margin: 0 }}>{t.teamManagement || "Team"}</h2>
      <button onClick={() => setShowAdd(!showAdd)} style={{ ...btnP(h), marginTop: 0, padding: `${sp.sm}px ${sp.lg}px`, fontSize: 12 }}>
        <Ic n="plus" s={14} c="#fff" /> {t.addMember || "Add Member"}
      </button>
    </div>

    {/* Add Member Form */}
    {showAdd && <div style={{ ...crd(h), marginBottom: sp.lg }} className="animate-slideUp">
      <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>{t.addTeamMember || "New Team Member"}</div>
      <Fld l={t.name || "Name"} v={name} set={setName} h={h} ph="e.g. Suresh Kumar" />
      <Fld l={t.phone || "Phone"} v={phone} set={setPhone} h={h} ph="+91 98765 43210" />
      <Fld l={t.password || "Password"} v={password} set={setPassword} h={h} type="password" ph="Set a password" />
      <div style={{ marginBottom: sp.md }}>
        <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.sm }}>{t.role || "Role"}</label>
        <div style={{ display: "flex", gap: sp.sm }}>
          {["manager", "viewer"].map(r => (
            <button key={r} onClick={() => setRole(r)} style={{
              flex: 1, padding: sp.sm, borderRadius: rad.sm,
              border: `1px solid ${role === r ? h.ac : h.bd}`,
              background: role === r ? `${h.ac}10` : "transparent",
              color: role === r ? h.ac : h.ts,
              cursor: "pointer", fontSize: 12, fontWeight: role === r ? 600 : 400
            }}>
              {r === "manager" ? "Manager" : "Viewer"}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", gap: sp.sm }}>
        <button onClick={handleAdd} disabled={saving} style={{ ...btnP(h), flex: 1, marginTop: 0, opacity: saving ? 0.6 : 1 }}>
          {saving ? "..." : (t.addMember || "Add Member")}
        </button>
        <button onClick={() => { setShowAdd(false); setName(""); setPhone(""); setPassword(""); }} style={{
          flex: 1, padding: sp.sm, borderRadius: rad.sm, border: `1px solid ${h.bd}`,
          background: "transparent", color: h.ts, cursor: "pointer", fontSize: 13
        }}>{t.cancel || "Cancel"}</button>
      </div>
    </div>}

    {/* Members List */}
    {members.length === 0 ? (
      <div style={{ ...crd(h), textAlign: "center", padding: sp.xxxl }}>
        <Ic n="users" s={32} c={h.tt} />
        <div style={{ ...type.sm, color: h.tt, marginTop: sp.md }}>{t.noTeamMembers || "No team members yet"}</div>
      </div>
    ) : (
      <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
        {members.map((m, i) => {
          const isMe = m.id === currUserId;
          const isOwner = m.role === "owner";
          return <div key={m.id} style={{ ...listRow(h, i === members.length - 1), flexDirection: "column", alignItems: "stretch" }}>
            <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
              <div style={icBox(isOwner ? h.ac : m.role === "manager" ? h.gn : h.wn, 36)}>
                <Ic n="user" s={16} c={isOwner ? h.ac : m.role === "manager" ? h.gn : h.wn} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
                  <span style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{m.name}</span>
                  <span style={roleBadge(m.role)}>{m.role.charAt(0).toUpperCase() + m.role.slice(1)}</span>
                  {isMe && <span style={bdg(`${h.ac}15`, h.ac)}>You</span>}
                  {!m.active && <span style={bdg(`${h.rd}15`, h.rd)}>Inactive</span>}
                </div>
                <div style={{ ...type.xs, color: h.tt }}>{m.phone}</div>
              </div>
              {!isOwner && !isMe && <div style={{ display: "flex", gap: sp.xs }}>
                <button onClick={() => { setEditId(editId === m.id ? null : m.id); setEditRole(m.role); }}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                  <Ic n="settings" s={14} c={h.ts} />
                </button>
                <button onClick={() => handleRemove(m)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                  <Ic n="x" s={14} c={h.rd} />
                </button>
              </div>}
            </div>

            {/* Edit Role */}
            {editId === m.id && <div style={{ marginTop: sp.sm, display: "flex", gap: sp.sm, alignItems: "center" }}>
              <select value={editRole} onChange={e => setEditRole(e.target.value)}
                style={{ ...inpS(h), flex: 1, marginBottom: 0 }}>
                <option value="manager">Manager</option>
                <option value="viewer">Viewer</option>
              </select>
              <button onClick={() => handleUpdateRole(m.id)} style={{
                padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: "none",
                background: h.ac, color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer"
              }}>{t.save || "Save"}</button>
              <button onClick={() => handleToggleActive(m)} style={{
                padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: "none",
                background: m.active ? `${h.rd}15` : `${h.gn}15`,
                color: m.active ? h.rd : h.gn,
                fontSize: 11, fontWeight: 600, cursor: "pointer"
              }}>{m.active ? "Deactivate" : "Activate"}</button>
            </div>}
          </div>;
        })}
      </div>
    )}

    {/* Role descriptions */}
    <div style={{ ...crd(h), marginTop: sp.lg }}>
      <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>Role Permissions</div>
      {[
        { role: "Owner", desc: "Full access — billing, settings, delete, team management", color: h.ac },
        { role: "Manager", desc: "Day-to-day — add/edit properties, tenants, payments, maintenance", color: h.gn },
        { role: "Viewer", desc: "Read-only — view dashboard, reports, export CSV", color: h.wn },
      ].map((r, i) => (
        <div key={i} style={{ display: "flex", gap: sp.md, alignItems: "flex-start", padding: `${sp.sm}px 0`, borderBottom: i < 2 ? `1px solid ${h.bd}` : "none" }}>
          <span style={bdg(`${r.color}15`, r.color)}>{r.role}</span>
          <span style={{ ...type.xs, color: h.ts, flex: 1 }}>{r.desc}</span>
        </div>
      ))}
    </div>
  </div>;
}
